import React, { memo, useCallback } from 'react';

import { Box, FormControl, FormGroup } from '@mui/material';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

import Checkbox from '../../form/Checkbox';
import HelperText from '../../form/HelperText';
import Label from '../../form/Label';
import { getPendingChangeOutlinedHighlightStyles } from '../../form/shared/styles';
import { getCheckboxGroupOptionTestId } from '../formGenerator.testIds';

const getWrapperStyles = ({ align, customStyles, isFieldChanged, columns }) => ({
  flexDirection: align === ItemGroupAlignment.HORIZONTAL ? 'row' : 'column',

  ...getConditionalObject(isFieldChanged, () => getPendingChangeOutlinedHighlightStyles(columns)),

  ...customStyles,
});

const FormGeneratorCheckboxGroup = ({
  label = '',
  options,
  value,
  disabled = false,
  onChange = _.noop,
  name = '',
  align = ItemGroupAlignment.HORIZONTAL,
  className,
  labelPlacement = ItemLabelPlacement.START,
  dataTestId,
  isMulti = false,
  error = false,
  helperText = '',
  required = false,
  customStyles,
  checkboxProps,
  isFieldChanged = false,
  columns,
}) => {
  const isOptionsExist = Array.isArray(options);

  const onCheckboxToggle = useCallback(
    optionValue => () => {
      const getUpdatedValue = () => {
        if (isMulti) {
          const { [optionValue]: currentValue, ...prevValues } = value ?? {};

          return {
            ...prevValues,
            [optionValue]: !currentValue,
          };
        }

        return !value;
      };

      const updatedValue = getUpdatedValue();

      onChange({
        target: {
          name,
          value: updatedValue,
        },
      });
    },
    [isMulti, name, value, onChange],
  );

  return (
    <Box
      className={clsx(
        'flex w-full justify-between',
        className,
        'form-generator__checkbox-group-container',
      )}
      sx={getWrapperStyles({ align, customStyles, isFieldChanged, columns })}
    >
      {label && (
        <Label error={error} name={name} required={required}>
          {label}
        </Label>
      )}

      {isOptionsExist && (
        <FormControl error={error}>
          <FormGroup
            aria-label={label}
            className="checkboxes-group"
            data-testid={dataTestId}
            id={name}
            row={align === ItemGroupAlignment.HORIZONTAL}
          >
            {options.map(option => {
              const isChecked = isMulti ? value?.[option.value] : value;

              return (
                <Checkbox
                  dataTestId={getCheckboxGroupOptionTestId(option.value)}
                  disabled={disabled}
                  error={error}
                  key={option.value}
                  label={option.name}
                  labelPlacement={labelPlacement}
                  value={isChecked}
                  onChange={onCheckboxToggle(option.value)}
                  {...checkboxProps}
                />
              );
            })}
          </FormGroup>
          <HelperText error={error}>{helperText}</HelperText>
        </FormControl>
      )}
    </Box>
  );
};

FormGeneratorCheckboxGroup.propTypes = {
  options: PropTypes.array.isRequired,
  columns: PropTypes.number.isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  align: PropTypes.string,
  customStyles: PropTypes.object,
  checkboxProps: PropTypes.object,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  isMulti: PropTypes.bool,
  label: PropTypes.string,
  labelPlacement: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
  onChange: PropTypes.func,
};

export default memo(FormGeneratorCheckboxGroup);
